import { config } from '../config';

/**
 * Centralized session storage management for room-related data
 * Uses config.storagePrefix to support multiple app variants
 */
class RoomSessionStorage {
  private static readonly PREFIX = config.storagePrefix;
  
  private static getKey(key: string): string {
    return `${this.PREFIX}-${key}`;
  }

  // Room name
  static getRoomName(): string | null {
    return sessionStorage.getItem(this.getKey('roomName'));
  }

  static setRoomName(name: string): void {
    sessionStorage.setItem(this.getKey('roomName'), name);
  }

  // Player name
  static getPlayerName(): string | null {
    return sessionStorage.getItem(this.getKey('playerName'));
  }

  static setPlayerName(name: string): void {
    sessionStorage.setItem(this.getKey('playerName'), name);
  }

  // Avatar
  static getAvatar(): string | null {
    return sessionStorage.getItem(this.getKey('avatar'));
  }

  static setAvatar(avatar: string): void {
    sessionStorage.setItem(this.getKey('avatar'), avatar);
  }

  // Host status (legacy flag for UI/session; prefer hostToken for reconnect emits)
  static getIsHost(): boolean {
    return sessionStorage.getItem(this.getKey('isHost')) === 'true';
  }

  static setIsHost(isHost: boolean): void {
    sessionStorage.setItem(this.getKey('isHost'), isHost ? 'true' : 'false');
  }

  // Host token — source of truth for host reconnection / get-room-state auth
  static getHostToken(): string | null {
    return sessionStorage.getItem(this.getKey('hostToken'));
  }

  static setHostToken(token: string): void {
    sessionStorage.setItem(this.getKey('hostToken'), token);
  }

  /** True when this tab can prove hostship (survives host play-too clearing isHost). */
  static hasHostToken(): boolean {
    return !!this.getHostToken();
  }

  // Premium room expiration date (timestamp in milliseconds)
  static getRoomExpiresAt(): number | null {
    const stored = sessionStorage.getItem(this.getKey('roomExpiresAt'));
    return stored ? parseInt(stored, 10) : null;
  }

  static setRoomExpiresAt(expiresAt: number): void {
    sessionStorage.setItem(this.getKey('roomExpiresAt'), expiresAt.toString());
  }

  static clearRoomExpiresAt(): void {
    sessionStorage.removeItem(this.getKey('roomExpiresAt'));
  }

  // Check if room has expired
  static isRoomExpired(): boolean {
    const expiresAt = this.getRoomExpiresAt();
    if (!expiresAt) {
      return false; // No expiration set, assume not expired
    }
    return Date.now() > expiresAt;
  }

  // Category votes (stored per game type and room)
  static getCategoryVotes(roomName: string, gameType: string): string[] {
    const key = this.getKey(`categoryVotes-${roomName}-${gameType}`);
    const stored = sessionStorage.getItem(key);
    return stored ? JSON.parse(stored) : [];
  }

  static setCategoryVotes(roomName: string, gameType: string, votes: string[]): void {
    const key = this.getKey(`categoryVotes-${roomName}-${gameType}`);
    sessionStorage.setItem(key, JSON.stringify(votes));
  }

  static clearCategoryVotes(roomName: string, gameType: string): void {
    const key = this.getKey(`categoryVotes-${roomName}-${gameType}`);
    sessionStorage.removeItem(key);
  }

  // Get all saved room data at once
  static getSavedRoomData(): {
    roomName: string | null;
    playerName: string | null;
    avatar: string | null;
    isHost: boolean;
    hostToken: string | null;
    expiresAt: number | null;
  } {
    return {
      roomName: this.getRoomName(),
      playerName: this.getPlayerName(),
      avatar: this.getAvatar(),
      isHost: this.getIsHost(),
      hostToken: this.getHostToken(),
      expiresAt: this.getRoomExpiresAt(),
    };
  }

  // Clear all room-related session storage
  static clearAll(): void {
    // Clear category votes before wiping roomName (keys are prefixed, not tied to a live roomName read)
    const categoryVotesPrefix = this.getKey('categoryVotes-');
    const voteKeys: string[] = [];
    for (let i = 0; i < sessionStorage.length; i++) {
      const key = sessionStorage.key(i);
      if (key && key.startsWith(categoryVotesPrefix)) {
        voteKeys.push(key);
      }
    }
    voteKeys.forEach((key) => sessionStorage.removeItem(key));

    const keys = ['roomName', 'playerName', 'avatar', 'isHost', 'hostToken', 'roomExpiresAt'];
    keys.forEach((key) => {
      sessionStorage.removeItem(this.getKey(key));
    });
  }

  // Clear specific items
  static clearRoomData(): void {
    sessionStorage.removeItem(this.getKey('roomName'));
  }

  /**
   * Clear room identity that must not leak across rooms (code, token, expiry, host flag).
   * Keeps playerName/avatar for re-join UX.
   */
  static clearRoomSessionIdentity(): void {
    this.clearRoomData();
    this.clearRoomExpiresAt();
    this.clearHostData();
  }

  /**
   * Clear room-related data but keep player name and avatar.
   * Use when join fails (e.g. room not found) so reconnection doesn't retry this room,
   * but the user doesn't have to re-enter their name when joining another room.
   */
  static clearRoomKeepPlayer(): void {
    this.clearRoomSessionIdentity();
  }

  static clearPlayerData(): void {
    sessionStorage.removeItem(this.getKey('playerName'));
    sessionStorage.removeItem(this.getKey('avatar'));
  }

  static clearHostData(): void {
    sessionStorage.removeItem(this.getKey('isHost'));
    sessionStorage.removeItem(this.getKey('hostToken'));
  }

  /**
   * Clear room identity from a previous session when it cannot be restored
   * (non-premium / expired). Keeps player name and avatar for re-join UX.
   */
  static clearStaleNonPremiumRoomSession(): void {
    const saved = this.getSavedRoomData();
    const savedExpired = saved.expiresAt != null && Date.now() > saved.expiresAt;
    const isPremiumRoom = saved.expiresAt != null && !savedExpired;
    if (saved.roomName && !isPremiumRoom) {
      this.clearRoomSessionIdentity();
    }
  }
}

export default RoomSessionStorage;
